import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge, useFormatShortDate } from '@insio/ui-toolkit';
import { plainDateFrom } from '@insio/dates';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { navigation } from '@/navigation';
import type { InvoiceType, PaymentMethod, PaymentStatus } from '@/graphql';
import { resolveHistoryEnumNamespace } from '@/features/invoices/utils/history-enum-label';
import { isValidInvoiceCardId } from '../invoice-card/invoice-card.types';
import { paymentStatusColor, validationStatusColor } from '../badge-colors';
import { useInvoiceCardStatus } from '../invoice-card/use-invoice-card-status';
import { CardActions, CardButton, ResultCard, ResultCardHeader } from '../ResultCard';
import type {
  InvoiceDetailPartType,
  InvoiceDetailLine,
  InvoiceDetailHistoryEntry
} from './invoice-detail.types';

interface Props {
  part: InvoiceDetailPartType;
}

/** Prefix of the invoice-detail page's history label keys — one source of truth for both views. */
const HISTORY_LABEL_PREFIX = 'features.invoices.detail.historyLabels';

function useShortDate(value: string | null): string | null {
  const fmt = useFormatShortDate();
  if (!value) return null;
  try {
    return fmt(plainDateFrom(value.slice(0, 10)));
  } catch {
    return value;
  }
}

function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className='flex items-baseline justify-between gap-sm'>
      <span className='text-foreground-muted text-sm shrink-0'>{label}</span>
      <span className='text-foreground-base text-sm text-right'>{value}</span>
    </div>
  );
}

function LinesTable({
  lines,
  t,
  currency
}: {
  lines: InvoiceDetailLine[];
  t: (k: string) => string;
  currency: string | null;
}) {
  const formatAmount = useFormatCurrency();
  if (lines.length === 0) return null;
  return (
    <div className='flex flex-col gap-2xs'>
      <span className='text-foreground-muted text-xs font-medium uppercase tracking-wide'>
        {t('invoiceDetail.lines')}
      </span>
      {lines.map((line, i) => (
        <div
          key={line.lineNumber ?? i}
          className='flex items-baseline justify-between gap-sm border-t border-border-base pt-2xs'
        >
          <span className='text-foreground-base text-sm truncate'>
            {line.lineNumber != null ? `${line.lineNumber}. ` : ''}
            {line.description ?? '—'}
          </span>
          <span className='text-foreground-base text-sm shrink-0'>
            {formatAmount(line.totalAmountInclVat, currency) ?? '—'}
          </span>
        </div>
      ))}
    </div>
  );
}

function HistoryList({
  entries,
  t
}: {
  entries: InvoiceDetailHistoryEntry[];
  t: (k: string) => string;
}) {
  const { t: tRoot, i18n } = useTranslation();
  const fmtDate = useFormatShortDate();
  // i18n.exists() keeps missing keys visible to tests instead of masking them with a fallback.
  const label = (subKey: string, raw: string) => {
    const key = `${HISTORY_LABEL_PREFIX}.${subKey}`;
    if (!i18n.exists(key)) return raw;
    const value = tRoot(key);
    return typeof value === 'string' ? value : raw;
  };
  /**
   * Status values travel as raw enum tokens (DRAFT, ERROR). Resolve them through the
   * shared enum namespaces so the card reads like the invoice detail page, not the database.
   * The namespace varies per entry, so useEnumTranslation (a hook) cannot serve this.
   */
  const enumValue = (value: string | null, entry: InvoiceDetailHistoryEntry): string => {
    if (!value) return '—';
    const namespace = resolveHistoryEnumNamespace(entry.eventType, entry.fieldName);
    if (namespace === null) return value;
    const key = `enums.${namespace}.${value}`;
    return i18n.exists(key) ? tRoot(key) : value;
  };
  const entryDate = (changedAt: string): string => {
    try {
      return fmtDate(plainDateFrom(changedAt.slice(0, 10)));
    } catch {
      return changedAt.slice(0, 10);
    }
  };
  if (entries.length === 0) return null;
  return (
    <div className='flex flex-col gap-2xs'>
      <span className='text-foreground-muted text-xs font-medium uppercase tracking-wide'>
        {t('invoiceDetail.history')}
      </span>
      {entries.map((entry, i) => (
        <div
          key={`${entry.eventType}-${entry.changedAt}-${i}`}
          className='flex flex-col gap-2xs border-t border-border-base pt-2xs'
        >
          <div className='flex items-baseline justify-between gap-sm'>
            <span className='text-foreground-base text-sm'>
              {label(`eventTypes.${entry.eventType}`, entry.eventType)}
            </span>
            <span className='text-foreground-muted text-xs'>{entryDate(entry.changedAt)}</span>
          </div>
          {entry.changedBy && (
            <span className='text-foreground-muted text-xs'>{entry.changedBy}</span>
          )}
          {entry.comment && (
            <span className='text-foreground-muted text-xs italic'>{entry.comment}</span>
          )}
          {entry.fieldName && (
            <span className='text-foreground-muted text-xs'>
              {label(`fieldLabels.${entry.fieldName}`, entry.fieldName)}:{' '}
              {enumValue(entry.fromValue, entry)} → {enumValue(entry.toValue, entry)}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function InvoiceDetailPart({ part }: Props) {
  const navigate = useNavigate();
  const t = useFeatureTranslation('aiChat');
  const statusOf = useInvoiceCardStatus();
  const tPayment = useEnumTranslation('paymentStatus');
  const tValidation = useEnumTranslation('validationStatus');
  const tDocumentType = useEnumTranslation('invoiceType');
  const tPaymentMethod = useEnumTranslation('paymentMethod');
  const formatAmount = useFormatCurrency();
  const [expanded, setExpanded] = useState(false);

  const invoiceDate = useShortDate(part.invoiceDate);
  const dueDate = useShortDate(part.dueDate);
  const taxDate = useShortDate(part.taxDate);
  const validationStatus = part.validationStatus ?? null;
  const status = statusOf(part);
  const paidDate = useShortDate(part.paidDate);

  const totalFormatted = formatAmount(part.totalAmountInclVat, part.currencyCode);
  const noVatFormatted = formatAmount(part.totalAmountNoVat, part.currencyCode);
  const vatFormatted = formatAmount(part.totalVatAmount, part.currencyCode);
  const paidFormatted = formatAmount(part.paidAmount, part.currencyCode);

  const hasLines = part.lines.length > 0;
  const hasHistory = part.history.length > 0;
  const hasExtra = hasLines || hasHistory;

  return (
    <ResultCard>
      <ResultCardHeader title={part.invoiceNumber ?? t('invoiceDetail.noNumber')}>
        <div className='flex items-center gap-xs'>
          {status && (
            <Badge color={status.color} size='sm'>
              {status.label}
            </Badge>
          )}
          {validationStatus && (
            <Badge color={validationStatusColor(validationStatus)} size='sm'>
              {tValidation(validationStatus)}
            </Badge>
          )}
        </div>
      </ResultCardHeader>

      {/* Core fields */}
      <div className='flex flex-col gap-2xs'>
        <Row label={t('invoiceDetail.supplier')} value={part.supplierName} />
        <Row label={t('invoiceDetail.supplierVatId')} value={part.supplierVatId} />
        <Row label={t('invoiceDetail.customer')} value={part.customerName} />
        <Row label={t('invoiceDetail.invoiceDate')} value={invoiceDate} />
        <Row label={t('invoiceDetail.dueDate')} value={dueDate} />
        <Row label={t('invoiceDetail.taxDate')} value={taxDate} />
        <Row
          label={t('invoiceDetail.documentType')}
          value={part.documentType ? tDocumentType(part.documentType as InvoiceType) : null}
        />
        <Row label={t('invoiceDetail.internalNumber')} value={part.internalNumber} />
        <Row label={t('invoiceDetail.description')} value={part.description} />
      </div>

      {/* Amounts */}
      <div className='flex flex-col gap-2xs border-t border-border-base pt-sm'>
        <Row label={t('invoiceDetail.totalNoVat')} value={noVatFormatted} />
        <Row label={t('invoiceDetail.totalVat')} value={vatFormatted} />
        {totalFormatted && (
          <div className='flex items-baseline justify-between gap-sm'>
            <span className='text-foreground-muted text-sm font-medium'>
              {t('invoiceDetail.total')}
            </span>
            <span className='text-foreground-base font-semibold'>{totalFormatted}</span>
          </div>
        )}
      </div>

      {/* Payment */}
      {(part.paymentStatus || part.paidAmount != null) && (
        <div className='flex flex-col gap-2xs border-t border-border-base pt-sm'>
          {part.paymentStatus && (
            <div className='flex items-center justify-between gap-sm'>
              <span className='text-foreground-muted text-sm'>
                {t('invoiceDetail.paymentStatus')}
              </span>
              <Badge color={paymentStatusColor(part.paymentStatus)} size='sm'>
                {tPayment(part.paymentStatus as PaymentStatus)}
              </Badge>
            </div>
          )}
          <Row label={t('invoiceDetail.paidAmount')} value={paidFormatted} />
          <Row label={t('invoiceDetail.paidDate')} value={paidDate} />
          <Row
            label={t('invoiceDetail.paymentMethod')}
            value={part.paymentMethod ? tPaymentMethod(part.paymentMethod as PaymentMethod) : null}
          />
        </div>
      )}

      {/* Expandable: lines + history */}
      {expanded && hasExtra && (
        <div className='flex flex-col gap-sm border-t border-border-base pt-sm'>
          <LinesTable lines={part.lines} t={t} currency={part.currencyCode} />
          <HistoryList entries={part.history} t={t} />
        </div>
      )}

      {/* Expand is the supporting action; opening the invoice is the main one. */}
      {(hasExtra || isValidInvoiceCardId(part.invoiceId)) && (
        <CardActions>
          {hasExtra && (
            <CardButton tone='outline' onClick={() => setExpanded(v => !v)}>
              {expanded
                ? t('invoiceDetail.collapse')
                : t('invoiceDetail.expand', {
                    count: part.lines.length + part.history.length
                  })}
            </CardButton>
          )}
          {isValidInvoiceCardId(part.invoiceId) && (
            <CardButton onClick={() => navigate(navigation.invoiceDetail(part.invoiceId))}>
              {t('openInvoice')}
            </CardButton>
          )}
        </CardActions>
      )}
    </ResultCard>
  );
}
